(function (root) {
  'use strict';
  if (root.JELivePersonalize) return;
  const clone = value => JSON.parse(JSON.stringify(value));
  const fail = code => { throw Object.assign(new Error(code), { code }); };
  const normal = value => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const id = value => /^[1-9]\d{0,23}$/.test(String(value));
  const placements = ['back-vertical', 'back-horizontal', 'front-horizontal', 'front-vertical'];
  const FROST_ID = '8964291952893';
  const FROST_PROFILE_ID = '15293885579517';
  // These published cup photos are pale even though their catalog swatches miss
  // Studio's conservative light-surface threshold. Reuse its brushed steel;
  // keep medium/dark photos (including Out of the Blue) on the original finish.
  const paleCupColors = new Map([
    ['9040176382205', new Set(['rose quartz'])],
    ['15298350121213', new Set(['rose quartz'])],
    ['8963490513149', new Set(['rose quartz'])],
    ['15298352840957', new Set(['rose quartz'])],
    ['15343486664957', new Set(['cape taupe'])]
  ]);
  const stateKeys = ['type', 'text', 'font', 'placement', 'scale', 'layout'];
  const supportedFonts = new Set(['Halimum', 'Graduate', 'Birthstone', 'Chewy', 'Black Ops One', 'Racing Sans One', 'Sacramento', 'Cookie', 'Handlee', 'Luxurious Script', 'Lavishly Yours', 'Send Flowers', 'Dancing Script', 'Pacifico', 'Great Vibes', 'Allura', 'Satisfy', 'Caveat', 'Kalam', 'Patrick Hand', 'Permanent Marker', 'Fredoka', 'Baloo 2', 'Lilita One', 'Bubblegum Sans', 'Bebas Neue', 'Anton', 'Oswald', 'Bangers', 'Playfair Display', 'Cormorant Garamond', 'Cinzel', 'Lora', 'Montserrat', 'Poppins', 'Quicksand']);

  function sourceOptions(product, variant) {
    const names = (product.options || []).map(option => normal(typeof option === 'string' ? option : option.name));
    const option = namesWanted => {
      const index = names.findIndex(name => namesWanted.includes(name));
      return index < 0 ? '' : variant.options?.[index] || variant[`option${index + 1}`] || '';
    };
    return { size: option(['size']), color: option(['color', 'colour']) || variant.title };
  }

  async function sourceProduct(config, signal) {
    const handle = config.sourceHandle || config.sourceProduct?.handle;
    const expectedId = String(config.sourceProductId || config.sourceProduct?.id || config.productId || '');
    const rootUrl = config.rootUrl || '/';
    if (!/^[a-z0-9][a-z0-9-]{0,254}$/.test(handle || '') || !id(expectedId) || !/^\/(?:[a-zA-Z0-9_-]+\/)*$/.test(rootUrl)) fail('catalog_error');
    const request = new AbortController();
    const abort = () => request.abort();
    const timeout = setTimeout(abort, 20000);
    if (signal?.aborted) abort();
    signal?.addEventListener('abort', abort, { once: true });
    try {
      const response = await fetch(`${rootUrl}products/${handle}.js`, { credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal: request.signal });
      if (!response.ok || response.redirected) fail('catalog_error');
      const text = await response.text();
      if (text.length > 3000000) fail('catalog_error');
      const product = JSON.parse(text);
      if (String(product.id) !== expectedId || product.handle !== handle || !Array.isArray(product.variants) || product.requires_selling_plan) fail('catalog_error');
      return product;
    } finally {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', abort);
    }
  }

  async function loadCatalog(config, { signal } = {}) {
    if (!root.EngravingCupLibrary) fail('dependencies_error');
    const source = await sourceProduct(config, signal);
    const isFrost = String(source.id) === FROST_ID;
    // Give the manifest and profile-product lookup one shared 20s allowance.
    // The preceding source-product request has its own separate 20s limit.
    const result = await root.EngravingCupLibrary.load({ products: [], productId: isFrost ? FROST_PROFILE_ID : String(source.id), rootUrl: config.rootUrl || '/', enabled: true, signal, timeoutMs: 20000 });
    if (!['loaded', 'partial'].includes(result.status)) fail('catalog_error');
    const profiles = result.products.flatMap(product => product.variants);
    const variants = source.variants.map(variant => {
      const options = sourceOptions(source, variant);
      const matches = profiles.filter(profile => isFrost ? normal(profile.color) === normal(options.color) : String(profile.id) === String(variant.id));
      const profile = matches.length === 1 ? matches[0] : null;
      const validProfile = Boolean(profile && (!isFrost || root.JELivePersonalizeFrost?.sourceForVariant(String(variant.id))));
      const allowedPlacements = validProfile ? placements.filter(placement => placement.startsWith('back') ? profile.backEnabled && !profile.backPreviewOnly : isFrost || !profile.frontPreviewOnly) : [];
      return {
        ...(validProfile ? clone(profile) : {}), id: String(variant.id), title: variant.title,
        ...options, options: [...variant.options], price: variant.price, available: Boolean(variant.available),
        previewAvailable: allowedPlacements.length > 0, allowedPlacements,
        sourceVariantId: String(variant.id), sourceProductId: String(source.id),
        ...(isFrost && validProfile ? { manualSizingReview: true, sourceProfileProductId: FROST_PROFILE_ID, sourceProfileVariantId: String(profile.id) } : {})
      };
    });
    if (!variants.some(variant => variant.previewAvailable)) fail('catalog_error');
    const first = variants.find(variant => variant.previewAvailable);
    return [{ id: String(source.id), title: source.title, handle: source.handle, url: `${config.rootUrl || '/'}products/${source.handle}`, image: first.image, area: clone(first.area), imageCrop: clone(first.imageCrop), physical: clone(first.physical), variants, manualSizingReview: isFrost }];
  }

  function checkedState(value, fonts, exampleAllowed, strings) {
    if (!value || !id(value.productId) || !id(value.variantId)) fail('unavailable');
    if (!['text', 'logo', 'both'].includes(value.type) || !placements.includes(value.placement)) fail('design_invalid');
    if (!Number.isInteger(value.quantity) || value.quantity < 1 || value.quantity > 99) fail('quantity_invalid');
    if (!Number.isFinite(value.scale) || value.scale < 50 || value.scale > 100) fail('design_invalid');
    const text = String(value.text || '').replace(/\r\n?/g, '\n');
    if (Array.from(text).length > 240 || text.split('\n').length > 8 || /[\u0000-\u0008\u000b\u000c\u000e-\u001f]/.test(text)) fail('text_limit');
    const textNeeded = value.type !== 'logo', logoNeeded = value.type !== 'text';
    if (textNeeded && !fonts.has(value.font)) fail('font_error');
    let example = false;
    if (textNeeded && !text.trim()) { if (!exampleAllowed) fail('text_required'); example = true; }
    if (logoNeeded && !value.logo) { if (!exampleAllowed) fail('logo_required'); example = true; }
    if (value.logo && (!idSafe(value.logo.id) || typeof value.logo.original !== 'string' || value.logo.original.length > 15000000 || !/^data:image\/(?:png|jpeg|svg\+xml);base64,[A-Za-z0-9+/]+=*$/.test(value.logo.original))) fail('artwork_invalid');
    const layout = checkedLayout(value.layout);
    return { ...value, layout, productId: String(value.productId), variantId: String(value.variantId), text, example,
      displayText: text.trim() ? text : strings.example_name || 'Your name', textNeeded, logoNeeded };
  }
  function idSafe(value) { return typeof value === 'string' && /^[A-Za-z0-9_-]{1,80}$/.test(value); }
  const quarterRotation = value => ((value % 360) + 360) % 360;
  const engineRotation = value => quarterRotation(value + 180) - 180;
  function checkedLayout(value) {
    if (value == null) return undefined;
    if (typeof value !== 'object' || Array.isArray(value)) fail('design_invalid');
    const result = {};
    for (const [key, item] of Object.entries(value)) {
      if (!['live-text', 'live-logo'].includes(key) || !item || ![item.x, item.y, item.scale].every(Number.isFinite) || item.x < 0 || item.x > 100 || item.y < 0 || item.y > 100 || item.scale < .01 || item.scale > 100) fail('design_invalid');
      result[key] = { x: item.x, y: item.y, scale: item.scale };
      if (item.rotation !== undefined) {
        if (![0, 90, 180, 270].includes(item.rotation)) fail('design_invalid');
        result[key].rotation = item.rotation;
      }
    }
    return result;
  }

  function create({ config, products, fonts }) {
    const Studio = customElements.get('engraving-studio');
    if (!Studio || !root.EngravingSizing || !root.EngravingExport || !root.EngravingServices) fail('dependencies_error');
    const fontList = (fonts || []).filter(font => supportedFonts.has(font.value));
    const fontSet = new Set(fontList.map(font => font.value));
    const host = Object.create(Studio.prototype);
    Object.defineProperty(host, 'dataset', { value: {} });
    const orderConfig = config.order || config;
    const entities = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", '#39': "'", '#x27': "'" };
    Object.assign(host, { config: orderConfig, strings: Object.fromEntries(Object.entries(orderConfig.strings || {}).map(([key, value]) => [key, String(value).replace(/&(amp|lt|gt|quot|apos|#39|#x27);/g, (_, entity) => entities[entity])])), products, assets: new Map(), fonts: fontList, fontSet, templates: [] });
    const images = new Map();
    const originalLoad = Studio.prototype.loadImage;
    host.loadImage = url => {
      if (!images.has(url)) images.set(url, originalLoad.call(host, url).catch(error => { images.delete(url); throw error; }));
      return images.get(url);
    };

    function selected(state) {
      const product = products.find(item => String(item.id) === String(state.productId));
      const variant = product?.variants.find(item => String(item.id) === String(state.variantId));
      if (!variant?.previewAvailable || !variant.allowedPlacements.includes(state.placement)) fail('preview_unavailable');
      return { product, variant };
    }

    function build(state) {
      const side = state.placement.startsWith('back') ? 'back' : 'front';
      const rotation = state.placement.endsWith('vertical') ? -90 : 0;
      const layers = [], assets = [];
      if (state.textNeeded || !state.logo) layers.push({ id: 'live-text', type: 'text', text: state.type === 'logo' ? config.strings?.example_logo || 'Your logo' : state.displayText, font: state.textNeeded ? state.font : fontList.find(font => font.value === 'Graduate')?.value || fontList[0]?.value, x: 50, y: 50, size: 80, rotation });
      if (state.logoNeeded && state.logo) {
        assets.push(clone(state.logo));
        layers.push({ id: 'live-logo', type: 'logo', assetId: state.logo.id, x: 50, y: 50, size: 70, rotation });
      }
      return { kind: 'engraving-studio', version: 1, productId: state.productId, variantId: state.variantId, sides: { front: side === 'front' ? layers : [], back: side === 'back' ? layers : [] }, assets };
    }

    function fittedSize(renderer, layer, side, scale, mixed) {
      const area = renderer.area(side);
      const geometry = renderer.dimensions({ ...layer, size: 100 }, side, false);
      const maxWidth = area.width * (mixed ? .86 : .91);
      const maxHeight = area.height * (mixed ? layer.type === 'logo' ? .47 : .33 : .91);
      const factor = Math.min(maxWidth / geometry.boundWidth, maxHeight / geometry.boundHeight) * scale / 100;
      if (!Number.isFinite(factor) || factor <= 0) fail('design_invalid');
      return Math.max(.01, Math.min(10001, 100 * factor));
    }
    function fit(renderer, state) {
      const side = state.placement.startsWith('back') ? 'back' : 'front';
      const area = renderer.area(side), layers = renderer.sides[side];
      const mixed = layers.length === 2, bases = {};
      for (const layer of layers) {
        const position = state.layout?.[layer.id];
        if (position?.rotation !== undefined) layer.rotation = engineRotation(position.rotation);
        layer.size = fittedSize(renderer, layer, side, state.scale, mixed);
        layer.x = 50; layer.y = mixed ? layer.type === 'logo' ? 30 : 75 : 47;
        renderer.constrain(layer, side);
        bases[layer.id] = layer.size;
        if (position) {
          layer.size *= position.scale; layer.x = position.x; layer.y = position.y;
          // Bake the shared renderer's bounded dimensions into saved geometry.
          layer.size = renderer.dimensions(layer, side).width / area.width * 100;
          renderer.constrain(layer, side);
        }
      }
      renderer.constrainSide(side);
      renderer.metallicCache = null;
      return bases;
    }

    function geometry(prepared) {
      const renderer = prepared.renderer, side = prepared.interactionSide;
      return { side, area: { ...renderer.area(side) }, layers: renderer.sides[side].map(layer => ({ id: layer.id, type: layer.type, ...renderer.dimensions(layer, side) })) };
    }
    function beginTransform(prepared, layerId) {
      const renderer = prepared.renderer, side = prepared.interactionSide;
      const layer = renderer.sides[side].find(value => value.id === layerId);
      if (!layer) fail('design_invalid');
      return { owner: prepared, selection: renderer.captureSelection([layer], side), layoutBases: { ...prepared.layoutBases }, layoutScales: { ...prepared.layoutScales } };
    }
    function transform(prepared, base, { dx = 0, dy = 0, scale = 1, rotate = 0 } = {}) {
      if (base?.owner !== prepared || ![dx, dy, scale].every(Number.isFinite) || scale <= 0 || !Number.isSafeInteger(rotate) || rotate % 90 !== 0) fail('design_invalid');
      const renderer = prepared.renderer, selection = base.selection;
      const original = selection.members[0].layer, baseline = base.layoutBases[original.id];
      const layer = renderer.sides[selection.side].find(item => item.id === original.id);
      if (rotate) {
        // Fit each orientation from the same chosen scale, rather than rotating
        // an already constrained size and progressively shrinking the design.
        const rotation = engineRotation(Math.round(original.rotation / 90) * 90 + quarterRotation(rotate));
        const nextBase = fittedSize(renderer, { ...original, rotation }, selection.side, prepared.fitScale, renderer.sides[selection.side].length === 2);
        const intention = Math.max(.01, Math.min(100, base.layoutScales[original.id] * scale));
        const area = renderer.area(selection.side);
        Object.assign(layer, { rotation, size: nextBase * intention, x: original.x + dx / area.width * 100, y: original.y + dy / area.height * 100 });
        layer.size = renderer.dimensions(layer, selection.side).width / area.width * 100;
        renderer.constrain(layer, selection.side);
        prepared.layoutBases[original.id] = nextBase;
        prepared.layoutScales[original.id] = intention;
      } else {
        const minimum = baseline * .01 / original.size, maximum = baseline * 100 / original.size;
        renderer.transformSelection(selection, { x: selection.geometry.x + dx, y: selection.geometry.y + dy, scale: Math.max(minimum, Math.min(maximum, scale)), turn: 0 });
        prepared.layoutBases[original.id] = baseline;
        prepared.layoutScales[original.id] = scale === 1 ? base.layoutScales[original.id] : layer.size / baseline;
      }
      renderer.metallicCache = null;
      return geometry(prepared);
    }
    function cancelTransform(prepared, base) {
      if (base?.owner !== prepared) fail('design_invalid');
      for (const member of base.selection.members) {
        const index = prepared.renderer.sides[base.selection.side].findIndex(layer => layer.id === member.layer.id);
        if (index >= 0) prepared.renderer.sides[base.selection.side][index] = clone(member.layer);
      }
      prepared.layoutBases = { ...base.layoutBases };
      prepared.layoutScales = { ...base.layoutScales };
      prepared.renderer.metallicCache = null;
      return geometry(prepared);
    }
    function commitTransform(prepared, value) {
      const layout = { ...checkedLayout(value.layout) };
      const side = prepared.interactionSide;
      for (const layer of prepared.renderer.sides[side]) {
        const scale = prepared.layoutScales[layer.id];
        layout[layer.id] = { x: layer.x, y: layer.y, scale: Math.max(.01, Math.min(100, scale)) };
        if (layer.rotation % 90 === 0) layout[layer.id].rotation = quarterRotation(layer.rotation);
      }
      return { layout: checkedLayout(layout) };
    }
    function draw(prepared, canvas) {
      canvas.width = canvas.height = 1000;
      prepared.renderer.draw(canvas.getContext('2d'), prepared.interactionSide, true);
    }

    async function customerPreview(renderer) {
      const sides = ['front', 'back'].filter(side => renderer.sides[side].length);
      if (sides.length === 1) return { side: sides[0], blob: await renderer.proofBlob(sides[0]) };
      if (sides.length !== 2) fail('design_invalid');
      // A restored source may contain both sides. Compose display proofs only;
      // the native layers and every production export stay separate and editable.
      const canvas = document.createElement('canvas');canvas.width = 2000;canvas.height = 1000;
      const context = canvas.getContext('2d');
      for (const [index, side] of sides.entries()) {
        const view = document.createElement('canvas');view.width = view.height = 1000;
        renderer.draw(view.getContext('2d'), side, true);context.drawImage(view, index * 1000, 0);
      }
      const blob = await new Promise((resolve, reject) => canvas.toBlob(value => value ? resolve(value) : reject(new Error('export_error')), 'image/png'));
      return { side: 'both', blob };
    }

    async function prepareInternal(value, forDisplay) {
      const state = checkedState(value, fontSet, forDisplay, config.strings || {});
      const { product, variant } = selected(state);
      if (!forDisplay) {
        const current = await sourceProduct(config);
        const fresh = current.variants.find(item => String(item.id) === state.variantId);
        if (!fresh || !fresh.available) { variant.available = false; fail('unavailable'); }
        if (!Number.isSafeInteger(fresh.price) || fresh.price < 0) fail('catalog_error');
        if (fresh.price !== variant.price) { variant.price = fresh.price; fail('price_changed'); }
      }
      let design = state.sourceDocument ? clone(state.sourceDocument) : build(state);
      if (String(design.productId) !== state.productId || String(design.variantId) !== state.variantId || state.sourceDocument && !design.livePersonalizer) fail('design_invalid');
      const prepared = await host.prepareRepeatDocument(design, [], state.quantity, { forRestore: true });
      if (paleCupColors.get(String(product.id))?.has(normal(variant.color))) {
        prepared.renderer.lightCupFinish = () => true;
        prepared.renderer.metallicCache = null;
      }
      host.assets = prepared.renderer.assets;
      if (!state.sourceDocument) prepared.layoutBases = fit(prepared.renderer, state);
      else {
        // Legacy/cart documents keep their exact saved geometry. Only compute the
        // default-fit denominators needed if the shopper deliberately edits them.
        const sides = prepared.renderer.sides;
        try {
          prepared.renderer.sides = build(state).sides;
          for (const side of ['front', 'back']) for (const layer of prepared.renderer.sides[side]) {
            const savedLayer = sides[side].find(item => item.id === layer.id);
            if (savedLayer) layer.rotation = savedLayer.rotation;
          }
          prepared.layoutBases = fit(prepared.renderer, { ...state, layout: undefined });
        } finally { prepared.renderer.sides = sides; prepared.renderer.metallicCache = null; }
      }
      prepared.interactionSide = state.placement.startsWith('back') ? 'back' : 'front';
      prepared.fitScale = state.scale;
      prepared.layoutScales = Object.fromEntries(prepared.renderer.sides[prepared.interactionSide].map(layer => {
        const position = state.layout?.[layer.id];
        const keepIntention = !state.sourceDocument || position?.rotation === quarterRotation(layer.rotation);
        return [layer.id, keepIntention ? position?.scale ?? 1 : layer.size / prepared.layoutBases[layer.id]];
      }));
      const saved = prepared.renderer.buildDocument(false);
      saved.livePersonalizer = { version: 1, ...Object.fromEntries(stateKeys.map(key => [key, state[key]])), listingUrl: config.listingUrl || `/products/${config.listingHandle || ''}` };
      prepared.document = saved;
      prepared.quantity = state.quantity;
      prepared.example = state.example;
      prepared.price = variant.price * state.quantity;
      prepared.manualSizingReview = product.manualSizingReview === true;
      if (!forDisplay) {
        if (state.example) fail('text_required');
        if (product.manualSizingReview) {
          if (!root.JELivePersonalizeFrost?.validate) fail('dependencies_error');
          root.JELivePersonalizeFrost.validate(prepared);
        } else {
          const problem = prepared.renderer.problem(true);
          if (problem) fail(problem);
        }
      }
      prepared.preview = await customerPreview(prepared.renderer);
      prepared.proofs = [prepared.preview];
      return prepared;
    }

    async function prepareLogo(original) {
      // Website choices and local files use the same bounded, automatic path.
      const saved = typeof original?.original === 'string';
      const file = saved ? host.originalFile(original) : original;
      const asset = await host.prepareAsset(file, saved ? original.id : undefined, undefined, { autoPrepare: true });
      // Stage metadata only. The caller commits after its lifecycle guard, and
      // preview owns caching so cancelled or timed-out work retains no asset.
      return { id: asset.id, name: asset.name, mime: asset.mime, original: asset.original, options: clone(asset.options), ...(asset.analysis?.autoPrepared ? { autoPrepared: true } : {}) };
    }

    async function form(prepared) {
      if (!prepared || prepared.example || !prepared.document?.livePersonalizer || !Number.isInteger(prepared.quantity)) fail('design_invalid');
      if (!prepared.manualSizingReview) {
        const problem = prepared.renderer.problem(true);
        if (problem) fail(problem);
      }
      const result = prepared.manualSizingReview
        ? await root.JELivePersonalizeFrost.form(prepared)
        : await prepared.renderer.cartForm(prepared.document, prepared.quantity, prepared.document.sides);
      result.set('properties[_Live personalizer]', 'v1');
      const listingUrl = config.listingUrl || `/products/${config.listingHandle || ''}`;
      if (!/^\/(?:[a-zA-Z0-9_-]+\/)*products\/[a-z0-9][a-z0-9-]{0,254}$/.test(listingUrl)) fail('design_invalid');
      result.set('properties[_Live personalizer URL]', listingUrl);
      return result;
    }

    async function restore(document, quantity) {
      const metadata = document?.livePersonalizer;
      if (metadata?.version !== 1) fail('cart_restore_unavailable');
      const state = { productId: String(document.productId), variantId: String(document.variantId), quantity, ...Object.fromEntries(stateKeys.map(key => [key, metadata[key]])), sourceDocument: clone(document) };
      if (state.type !== 'text') {
        const logos = document.assets || [];
        if (logos.length !== 1) fail('cart_restore_unavailable');
        state.logo = clone(logos[0]);
      }
      const prepared = await prepareInternal(state, true);
      if (prepared.example) fail('cart_restore_unavailable');
      return { state, prepared };
    }

    return Object.freeze({ prepareLogo, preview: state => prepareInternal(state, true), prepare: state => prepareInternal(state, false), form, restore, geometry, beginTransform, transform, cancelTransform, commitTransform, draw });
  }
  root.JELivePersonalize = Object.freeze({ loadCatalog, create });
})(globalThis);
